import {inject, Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {map, switchMap, tap} from 'rxjs/operators';
import {
  PaymentGateway,
  PaymentPollingStatus,
  PaymentVerificationResult
} from '@features/payment/domain/model/payment.model';
import {PaymentStrategy} from '@features/payment/domain/ports/strategy/payment-strategy.port';
import {Payment} from '@features/payment/domain/entities/payment.entity';
import {PaymentHardwareBridge} from '@features/payment/infrastructure/services/payment-hardware.bridge';
import {BatchPaymentsGateway} from '@features/payment/infrastructure/gateways/batch-payments.gateway';
import {environment} from '@env/environment';
import {CheckoutGateway} from '../../gateways/checkout.gateway';
import {CheckoutPayloadMapper} from '../../mappers/checkout-payload.mapper';
import {PaymentPollingEngine} from '../../services/payment-polling.engine';
import {PaymentStatusTranslator} from '../../services/payment-status.translator';

@Injectable({providedIn: 'root'})
export class HardwareIntegratedStrategy implements PaymentStrategy {
  private readonly checkoutGateway = inject(CheckoutGateway);
  private readonly engine = inject(PaymentPollingEngine);
  private readonly batch = inject(BatchPaymentsGateway);
  private readonly mapper = inject(CheckoutPayloadMapper);
  private readonly translator = inject(PaymentStatusTranslator);
  private readonly tapToPay = inject(PaymentHardwareBridge);

  checkout(payments: Payment[]): Observable<string> {
    return this.tapToPay.getInstallationId().pipe(
      tap(id => this.assignSourceId(payments, id)),
      switchMap(() => this.checkoutGateway.checkoutOnline(this.mapper.toCheckoutPayload(payments)))
    );
  }

  verify(payment: Payment): Observable<PaymentVerificationResult> {
    if (!payment.transactionId) {
      throw new Error(`[HardwareIntegratedStrategy] Missing transactionId for verification`);
    }

    return this.engine.poll(payment.transactionId).pipe(
      switchMap(status => this.handleHardwareStep(status, payment.transactionId!)),
      map(status => this.translator.translate(status))
    );
  }

  private handleHardwareStep(status: PaymentPollingStatus, transactionId: string): Observable<PaymentPollingStatus> {
    if (status !== PaymentPollingStatus.WAITING_OPEN_TAP_TO_PAY) {
      return of(status);
    }

    return this.batch.getPhysicalPaymentSecret(transactionId).pipe(
      switchMap(secret => this.tapToPay.execute({
        secret,
        useCardReader: false,
        baseUrl: environment.physicalPaymentsServiceURL,
      })),
      switchMap(() => this.engine.poll(transactionId))
    );
  }

  private assignSourceId(payments: Payment[], id: string): void {
    payments
      .filter(p => p.gateway === PaymentGateway.TAP_TO_PAY)
      .forEach(p => p.assignSourceId(id));
  }
}
